import React from 'react'
import {useParams} from 'react-router-dom'
import {useSolv, useTenant} from "./components/SolvProvider";
import {useIntlEx} from "./components/IntlUtils.js"
import MainMenu from "./MainMenu.js"
import {
  ActionButton,
  DropdownMenu,
  DropdownMenuItem,
  FormBody,
  FormHeader,
  MainContainer
} from "./components/FormComps.js"
import {AccountStatusLabel, AccountTabs, useResellerType, useTenantPlan} from "./components/AccountUtils"
import {UserLink} from "./components/UserUtils";
import {DataTable, useCols} from "./components/TableComps"
import ResellerInviteDialog from "./ResellerInviteDialog";
import {openDialogCurried} from "./components/DialogUtils";
import getLogger from "./components/Logging.js"
import './App.css'
import ExportToFileDialog from "./components/ExportToFileDialog";

const log = getLogger("Resellers")

export default function Resellers(props) {

  const params = useParams()

  const {api, session} = useSolv()
  const {intl} = useIntlEx()

  const {tenant} = useTenant(params.tenantId)

  const {tenantPlanOpts} = useTenantPlan("RESELLER")
  const {resellerTypeOpts, getListableResellerTypeOpts} = useResellerType()

  const {cols} = useCols({
    specs: [
      {
        field: "tenantName",
        label: intl.msg("account"),
        filter: "text",
        sort: "ASC",
        align: "left",
        width: "md",
      },
      {
        field: "tenantPlanId",
        label: intl.msg("account_plan"),
        filter: {
          type: "select",
          multi: true,
          searchable: false,
          options: () => tenantPlanOpts
        },
        sort: "",
        align: "left",
        visible: () => (session.user.isSystem())
      },
      {
        field: "resellerTypeId",
        label: intl.msg("reseller_type"),
        filter: {
          type: "select",
          multi: true,
          searchable: false,
          options: () => getListableResellerTypeOpts(tenant)
        },
        sort: "",
        align: "left",
        width: "md",
        visible: () => (session.user.isSystem() || (tenant.maxDepth > 0))
      },
      {
        field: "parentNames",
        label: intl.msg("parent_tenant"),
        filter: "text",
        sort: "",
        align: "left",
        width: "md",
        visible: () => (session.user.isSystem() || tenant.parentId)
      },
      {
        field: "ownerDisplayName",
        label: intl.msg("owner"),
        filter: "text",
        sort: "",
        width: "md",
        align: "left"
      },
      {
        field: "regionId",
        label: intl.msg("region"),
        filter: {
          type: "asyncselect",
          loader: (input, loadedOptions, additional) => {
            // log.debug("listRegions: invoked")
            return api.listRegions({view: "BASIC", extraParams: `${input ? `filter=regionName:${encodeURIComponent(input)}&` : ""}sort=regionName:ASC`, cursor: additional?.cursor, limit: 20})
              .then(({data, nextCursor}) => {
                let opt = data.map(c => {
                  return {
                    value: c.regionId,
                    label: c.regionName,
                    pop: c.pop
                  };
                });
                let res = {
                  options: opt,
                }
                if (nextCursor) {
                  res = {
                    ...res,
                    hasMore: true,
                    additional: {
                      cursor: nextCursor
                    }
                  }
                }
                // log.debug("loadRegions: res=", res)
                return res
              });
          }
        },
        sort: "",
        align: "left",
        width: "md",
      },
      // {
      //   field: "industryId",
      //   label: intl.msg("industry"),
      //   filter: {
      //     type: "asyncselect",
      //     loader: (input, loadedOptions, additional) => {
      //       // log.debug("listIndustries: invoked")
      //       return api.listIndustries({view: "BASIC", extraParams: `${input ? `filter=industryName:${encodeURIComponent(input)}&` : ""}sort=industryName:ASC`, cursor: additional?.cursor, limit: 20})
      //         .then(({data, nextCursor}) => {
      //           let opt = data.map(c => {
      //             return {
      //               value: c.industryId,
      //               label: c.industryName,
      //               pop: c.pop
      //             };
      //           });
      //           let res = {
      //             options: opt,
      //           }
      //           if (nextCursor) {
      //             res = {
      //               ...res,
      //               hasMore: true,
      //               additional: {
      //                 cursor: nextCursor
      //               }
      //             }
      //           }
      //           // log.debug("loadIndustries: res=", res)
      //           return res
      //         });
      //     }
      //   },
      //   sort: "",
      //   align: "left",
      //   width: "md",
      //   visible: () => "SOLV" === brandId,
      // },
      {
        field: "status",
        label: intl.msg("status"),
        filter: {
          type: "select:accountStatus",
          multi: true,
          searchable: false,
        },
        sort: "",
        align: "left",
        width: "sm",
      },
    ],
    storageItemName: "resellers",
  })

  function handleFetchDataPage({pageParam: cursor}) {
    return api.listResellers({tenantId: tenant.tenantId, extraParams: cols.stringify(), cursor: cursor})
  }

  function handleRenderDataCol(col, row) {
    let n
    switch (col.field) {
      case "tenantName":
        n = <span>{row.tenantName}</span>
        break
      case "resellerTypeId":
        n = <span>{intl.msg(`reseller_type_${row.resellerTypeId?.toLowerCase()}`)}</span>
        break
      case "parentNames":
        n = row.parents ? (
              row.parents?.map((p, index) => {
                return (
                  <>
                    {
                      index > 0 ? (<span className="text-muted"> / </span>) : (<></>)
                    }
                    <a className="text-link" href={`/${p.tenantId}`}>
                       <span>{p.tenantName}</span>
                    </a>
                  </>
                )
              })
            ) : (<></>)
        break
      case "tenantPlanId":
        n = <span>{intl.msg(`account_plan_${row.tenantPlanId?.toLowerCase()}`)}</span>
        break
      case "ownerDisplayName":
        n = <div className="d-flex">
          {
            row.status !== "INVITING" ? (
              <UserLink user={row.owner} session={session} tenant={row}/>
            ) : (
              row.owner.displayName
            )
          }
        </div>
        break
      case "regionId":
        n = <span>{row.regionName}</span>
        break
      case "industryId":
        n = <span>{row.industryName}</span>
        break
      case "status":
        n = AccountStatusLabel(row)
        break
    }
    return (
      n
    )
  }

  function canAdd() {
    return tenant && session && session.user && (session.user.isSystemUser() || (session.user.isResellerUser() && tenant.hasSubTenants()))
  }

  function canExport() {
    return session && session.user && (session.user.isSystemUser() || session.user.isResellerUser())
  }

  function canMove() {
    return tenant.accessingAs("MANAGER/DOWNSTREAM/*")
  }

  function handleRowClick(row) {
    window.location = `/${row.tenantId}`
  }

  async function handleExport() {
    return api.exportResellers({tenantId: tenant.tenantId, extraParams: cols.stringify(), limit: -1})
  }

  function FormControls(props) {
    const ctls = []

    if (canAdd()) {
      ctls.push(<ActionButton name="add_new" flavor="primary" onClick={openDialogCurried("dlg_reseller_invite")}/>)
    }

    ctls.push(<MoreMenu/>)

    return ctls
  }

  function MoreMenu(props) {

    const ctls = []

    if (canExport()) {
      ctls.push(
        <DropdownMenuItem name="export" onClick={openDialogCurried("dlg_resellers_export")} prefixWithDivider={ctls.length > 0}/>
      )
    }

    return (
      ctls.length > 0 ? (
        <DropdownMenu>
          {ctls}
        </DropdownMenu>
      ) : (
        <></>
      )
    )
  }

  return (
    tenant &&
      <>
        <MainContainer tenant={tenant} menu={MainMenu} allowIf={(tenant.isSystem()) || (tenant.isReseller() && tenant.hasSubTenants())}>
          <FormHeader>
            <AccountTabs tenant={tenant}/>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                {intl.msg("resellers")}
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                <FormControls/>
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
          </FormHeader>
          <FormBody>
            <DataTable
              tenant={tenant}
              cols={cols}
              resourceName="resellers"
              dataKey="tenantId"
              onFetchDataPage={handleFetchDataPage}
              onRenderDataCol={handleRenderDataCol}
              onClickDataRow={handleRowClick}
            />
          </FormBody>
        </MainContainer>
        <ResellerInviteDialog tenant={tenant}/>
        <ExportToFileDialog
          key="download"
          type="resellers"
          fileName={`solv-resellers`}
          fileExt={"csv"}
          onExport={handleExport}/>
      </>
  )
}